import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "@headlessui/react";
import { Search, X, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Ingredient } from "../types/Types";

interface Props {
  search: string;
  setSearch: (val: string) => void;
  ingredients: Ingredient[];
  loading: boolean;
  page: number;
  totalPages: number;
  absoluteTotal: number;
  setPage: (p: number) => void;
  onSelect?: (ingredient: Ingredient) => void;
  currentIngredientId?: string; // Current ingredient ID for list-based nav
  currentIngredientCode?: string; // FIX 1: Explicit code prop — stable, not derived from list
  onModalOpen?: () => void; // Callback when modal opens to trigger initial fetch
}

const OSSearch: React.FC<Props> = ({
  search,
  setSearch,
  ingredients,
  loading,
  page,
  totalPages,
  absoluteTotal,
  setPage,
  onSelect,
  currentIngredientId,
  currentIngredientCode,
  onModalOpen,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasTriggeredModalOpen = useRef(false);

  // Type-to-jump: null = not editing (show current record); non-null = user is typing a code.
  const [typed, setTyped] = useState<string | null>(null);
  const pendingCode = useRef<string | null>(null);
  const pendingSearchStarted = useRef(false);

  // Code accessor — hoisted so both the Enter-to-jump handler and the results
  // table can use the same logic (was previously defined inline in the .map).
  const getCode = (ing: Ingredient): string => {
    if (typeof ing.items === 'string') return ing.items;
    return ing.items?.ident || '-';
  };

  // FIX 3: Keep highlightedIndex in a ref so the IntersectionObserver callback
  // can read the current value without being in its dependency array.
  const highlightedIndexRef = useRef(highlightedIndex);
  useEffect(() => {
    highlightedIndexRef.current = highlightedIndex;
  }, [highlightedIndex]);

  // FIX 4: Track the previous search value so we only reset the highlight when
  // the query actually changes, not on every page-1 background refetch.
  const prevSearchRef = useRef(search);

  // Handle highlighted index when ingredients change
  useEffect(() => {
    if (ingredients.length === 0) {
      setHighlightedIndex(-1);
      return;
    }

    // FIX 4: Only reset to 0 when the search query changed (new result set),
    // not on every page-1 fetch that happens during navigation or background reloads.
    const searchChanged = search !== prevSearchRef.current;
    prevSearchRef.current = search;

    setHighlightedIndex(prev => {
      if (prev === -1 || searchChanged) return 0;
      return prev < ingredients.length ? prev : ingredients.length - 1;
    });
  }, [ingredients]); // eslint-disable-line react-hooks/exhaustive-deps
  // NOTE: `search` intentionally omitted — we read prevSearchRef inside the effect.

  // Focus search input when modal opens and trigger initial fetch.
  // BUG 5 fix: a long setTimeout (150ms) left a window right after the modal
  // opened where the first characters typed were swallowed (autoFocus and the
  // deferred focus raced each other). Focus on the next animation frame
  // instead, so it lands as soon as the dialog commits; retry once on the
  // following frame in case Headless UI's own focus trap steals it back.
  useEffect(() => {
    if (isModalOpen && !hasTriggeredModalOpen.current) {
      hasTriggeredModalOpen.current = true;
      onModalOpen?.();

      let rafId2 = 0;
      const rafId1 = requestAnimationFrame(() => {
        searchInputRef.current?.focus();
        rafId2 = requestAnimationFrame(() => {
          if (document.activeElement !== searchInputRef.current) {
            searchInputRef.current?.focus();
          }
        });
      });

      return () => {
        cancelAnimationFrame(rafId1);
        if (rafId2) cancelAnimationFrame(rafId2);
      };
    } else if (!isModalOpen) {
      hasTriggeredModalOpen.current = false;
    }
  }, [isModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // FIX 3: Infinite scroll observer — highlightedIndex is NOT in deps; we read
  // it via highlightedIndexRef to avoid tearing down/recreating on every keypress.
  useEffect(() => {
    if (!loadingRef.current || loading || page >= totalPages || ingredients.length === 0) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading) {
          const currentHighlighted = highlightedIndexRef.current;
          setPage(page + 1);

          if (currentHighlighted >= 0) {
            setTimeout(() => {
              const element = document.querySelector(`[data-row-index="${currentHighlighted}"]`);
              if (element) {
                element.scrollIntoView({ behavior: 'auto', block: 'nearest' });
              }
            }, 100);
          }
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
        root: scrollContainerRef.current
      }
    );

    observer.observe(loadingRef.current);
    return () => observer.disconnect();
  }, [loading, page, totalPages, setPage, ingredients.length]);
  // NOTE: highlightedIndex deliberately excluded from deps (FIX 3).

  const [isAtFirstIngredient, setIsAtFirstIngredient] = useState(false);
  const [isAtLastIngredient, setIsAtLastIngredient] = useState(false);

  // FIX 2: Boundary detection — when the current ingredient is not in the visible
  // (possibly filtered/paged) list, findIndex returns -1.  Treat -1 as "unknown
  // position" → mark both boundary flags as false (buttons stay enabled, no wrong
  // jump).  Only mark a boundary when we have positive confirmation of position.
  useEffect(() => {
    if (!currentIngredientId || ingredients.length === 0) {
      setIsAtFirstIngredient(false);
      setIsAtLastIngredient(false);
      return;
    }

    const currentIndex = ingredients.findIndex(ing => ing._id === currentIngredientId);

    if (currentIndex === -1) {
      // Cannot determine position in filtered/paged list — disable neither button.
      setIsAtFirstIngredient(false);
      setIsAtLastIngredient(false);
      return;
    }

    // BUG 1 fix: the list is sorted ident-ASCENDING and is CUMULATIVE (page 1
    // is always fetched first, later pages are APPENDED), so:
    // - Index 0                         = globally first ident  → isAtFirstIngredient
    // - Last index once page === totalPages = globally last ident → isAtLastIngredient
    setIsAtFirstIngredient(currentIndex === 0);
    setIsAtLastIngredient(currentIndex === ingredients.length - 1 && page === totalPages);
  }, [currentIngredientId, ingredients, page, totalPages]);

  // BUG 1 fix: Navigation handlers rewritten for ident-ASCENDING, cumulative
  // paging.  Guard against findIndex returning -1 so we never make a wrong
  // jump — when position is unknown (filtered list), do nothing rather than
  // jumping to an incorrect record.
  const handleFirstIngredient = () => {
    // First = globally first ident = index 0 (page 1 is always loaded first).
    if (ingredients.length > 0) {
      onSelect?.(ingredients[0]);
    }
  };

  const handlePreviousIngredient = () => {
    if (!currentIngredientId) return;

    const currentIndex = ingredients.findIndex(ing => ing._id === currentIngredientId);

    if (currentIndex === -1) {
      // Position unknown (modal is filtered) — safe no-op to prevent wrong jump.
      return;
    }

    if (currentIndex > 0) {
      // Previous ident (lower index — list is ident-ascending)
      onSelect?.(ingredients[currentIndex - 1]);
    }
    // else: already at the first ident — isAtFirstIngredient is true, button disabled
  };

  const handleNextIngredient = () => {
    if (!currentIngredientId) return;

    const currentIndex = ingredients.findIndex(ing => ing._id === currentIngredientId);

    if (currentIndex === -1) {
      // Position unknown (modal is filtered) — safe no-op to prevent wrong jump.
      return;
    }

    if (currentIndex < ingredients.length - 1) {
      // Next ident already loaded (higher index — list is ident-ascending)
      onSelect?.(ingredients[currentIndex + 1]);
    } else if (page < totalPages) {
      // At the end of what's loaded — fetch the next page before jumping.
      setPage(page + 1);
    }
    // else: already at the last loaded ident on the last page — isAtLastIngredient is true, button disabled
  };

  const handleLastIngredient = () => {
    // Last = globally last ident. Keep loading pages until everything is in,
    // then jump to the last entry of the now-complete list.
    if (page < totalPages) {
      setPage(page + 1);
    } else if (ingredients.length > 0) {
      onSelect?.(ingredients[ingredients.length - 1]);
    }
  };

  const hasNoIngredients = !currentIngredientId;

  const handleKeyDownOnInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const newIndex = prev < ingredients.length - 1 ? prev + 1 : prev;
        setTimeout(() => {
          const element = document.querySelector(`[data-row-index="${newIndex}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 50);
        return newIndex;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const newIndex = prev > 0 ? prev - 1 : prev;
        setTimeout(() => {
          const element = document.querySelector(`[data-row-index="${newIndex}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 50);
        return newIndex;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && ingredients[highlightedIndex]) {
        onSelect?.(ingredients[highlightedIndex]);
        setIsModalOpen(false);
        setSearch("");
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsModalOpen(false);
      setSearch("");
    }
  };

  // FIX 1: Use the explicit currentIngredientCode prop as PRIMARY source for
  // the toolbar display.  Fall back to a list lookup only when the prop is empty
  // (e.g. nothing is loaded yet).  This keeps the toolbar stable regardless of
  // what is in the modal's filtered search list.
  const getToolbarDisplay = (): string => {
    if (!currentIngredientId) return "";

    // Primary: explicit prop threaded from the loaded ingredient
    if (currentIngredientCode) return currentIngredientCode;

    // Fallback: derive from list (works when current item happens to be in-page)
    const found = ingredients.find(ing => ing._id === currentIngredientId);
    if (!found) return "";
    if (typeof found.items === 'string') return found.items;
    return found.items?.ident || found.items?.name || '-';
  };

  // Resolve a typed code once the parent's list (re-filtered via setSearch)
  // comes back. 0 matches or >1 -> escalate to the modal, already filtered.
  useEffect(() => {
    const code = pendingCode.current;
    if (!code) return;
    if (loading && search.trim().toLowerCase() === code.toLowerCase()) {
      pendingSearchStarted.current = true;
      return;
    }
    if (loading) return;
    // setSearch clears the current rows before the debounced request starts.
    // Keep the pending code until that request has populated results; otherwise
    // the transient empty list is mistaken for a miss and opens the modal.
    if (ingredients.length === 0 && !pendingSearchStarted.current) return;
    pendingCode.current = null;
    pendingSearchStarted.current = false;
    const matches = ingredients.filter(ing => getCode(ing)?.toLowerCase() === code.toLowerCase());
    if (matches.length === 1) {
      onSelect?.(matches[0]);
      setTyped(null);
      setSearch("");
      return;
    }
    setIsModalOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  }, [ingredients, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative inline-flex items-center gap-0">
      {/* First ingredient button */}
      <button
        className="p-1.5 h-9 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-l-xl transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
        onClick={handleFirstIngredient}
        disabled={hasNoIngredients || isAtFirstIngredient}
        aria-label="First ingredient"
        title="First ingredient"
      >
        <ChevronsLeft className="w-4 h-4" />
      </button>

      {/* Previous ingredient button */}
      <button
        className="p-1.5 h-9 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
        onClick={handlePreviousIngredient}
        disabled={hasNoIngredients || isAtFirstIngredient}
        aria-label="Previous ingredient"
        title="Previous ingredient"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      {/* Search input with integrated search icon */}
      <div className="group relative" style={{ width: '15ch' }}>
        <input
          type="text"
          placeholder={t("search.searchNormativ") || "Пребарувај Норматив"}
          className="w-full pl-3 pr-9 py-2 h-9 border border-gray-300 bg-white shadow-sm focus:bg-blue-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-center font-semibold text-sm text-gray-800 cursor-text select-all"
          value={typed ?? getToolbarDisplay()}
          onChange={(e) => setTyped(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => {
            if (typed !== null && pendingCode.current === null) setTyped(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "F4") {
              e.preventDefault();
              if (typed && typed.trim()) {
                setSearch(typed.trim());
              }
              setIsModalOpen(true);
              setTimeout(() => searchInputRef.current?.focus(), 100);
              return;
            }
            if (e.key === "Escape") {
              setTyped(null);
              e.currentTarget.blur();
              return;
            }
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              const code = typed?.trim();
              if (!code) return;
              const matches = ingredients.filter(ing => getCode(ing)?.toLowerCase() === code.toLowerCase());
              if (matches.length === 1) {
                onSelect?.(matches[0]);
                setTyped(null);
                setSearch("");
                return;
              }
              pendingCode.current = code;
              pendingSearchStarted.current = false;
              setSearch(code);
              return;
            }
          }}
        />
        {/* Search icon - clickable to open modal */}
        <button
          type="button"
          onClick={() => {
            setIsModalOpen(true);
            setTimeout(() => searchInputRef.current?.focus(), 100);
          }}
          className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-blue-600 hover:text-gray-600 transition-colors"
          aria-label="Search"
        >
          <Search className="w-4 h-4" />
        </button>
      </div>

      {/* Next ingredient button */}
      <button
        className="p-1.5 h-9 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
        onClick={handleNextIngredient}
        disabled={hasNoIngredients || isAtLastIngredient}
        aria-label="Next ingredient"
        title="Next ingredient"
      >
        <ChevronRight className="w-4 h-4" />
      </button>

      {/* Last ingredient button */}
      <button
        className="p-1.5 h-9 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-r-xl transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
        onClick={handleLastIngredient}
        disabled={hasNoIngredients || isAtLastIngredient}
        aria-label="Last ingredient"
        title="Last ingredient"
      >
        <ChevronsRight className="w-4 h-4" />
      </button>

      {/* Modal */}
      <Dialog open={isModalOpen} onClose={() => {}} className="relative z-50">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[78vh] overflow-hidden flex flex-col border border-gray-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex flex-col">
                <Dialog.Title className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Search className="w-4 h-4 text-blue-600" />
                  {t("search.searchNormativ") || "Search Normativ"}
                </Dialog.Title>
                <p className="text-xs text-gray-600 mt-1">
                  {t("search.keyboardInstructions") || "Use arrow keys to navigate, Enter to select"}
                </p>
              </div>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setSearch("");
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 flex-1 flex flex-col overflow-hidden">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleKeyDownOnInput}
                  placeholder={t("search.searchNormativPlaceholder") || "Type to search normativ..."}
                  className="w-full pl-10 pr-4 h-10 border border-gray-300 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm"
                />
              </div>

              <div
                ref={scrollContainerRef}
                className="flex-1 overflow-y-auto border border-gray-200 rounded-lg"
                style={{ minHeight: '220px', maxHeight: '52vh', scrollBehavior: 'auto' }}
              >
                {loading && ingredients.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Searching...</span>
                  </div>
                ) : (
                  <table className="w-full border-collapse">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-left">
                        <th className="py-3 px-4 font-semibold text-gray-700 border-b">{t("tableHeaders.idents") || "Code"}</th>
                        <th className="py-3 px-4 font-semibold text-gray-700 border-b">{t("tableHeaders.name") || "Name"}</th>
                        <th className="py-3 px-4 font-semibold text-gray-700 border-b">{t("tableHeaders.batch") || "Batch"}</th>
                        <th className="py-3 px-4 font-semibold text-gray-700 border-b">{t("tableHeaders.version") || "Version"}</th>
                        <th className="py-3 px-4 font-semibold text-gray-700 border-b">{t("sidebar.status") || "Status"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ingredients.map((ingredient, idx) => {
                        // Helper to get name
                        const getName = (ing: Ingredient) => {
                          if (typeof ing.items === 'object' && ing.items) {
                            return ing.items.name || '-';
                          }
                          return '-';
                        };
                        return (
                          <tr
                            key={ingredient._id}
                            data-row-index={idx}
                            className={`border-b border-gray-100 cursor-pointer transition-all duration-200 ${
                              idx === highlightedIndex
                                ? "bg-blue-50 border-l-4 border-blue-500"
                                : "hover:bg-blue-50"
                            }`}
                            onMouseEnter={() => setHighlightedIndex(idx)}
                            onClick={() => {
                              onSelect?.(ingredient);
                              setIsModalOpen(false);
                              setSearch("");
                            }}
                          >
                            <td className={`py-3 px-4 font-medium transition-colors duration-200 ${
                              idx === highlightedIndex ? "text-blue-900" : "text-gray-900"
                            }`}>{getCode(ingredient)}</td>
                            <td className={`py-3 px-4 transition-colors duration-200 ${
                              idx === highlightedIndex ? "text-blue-800" : "text-gray-700"
                            }`}>{getName(ingredient)}</td>
                            <td className={`py-3 px-4 transition-colors duration-200 ${
                              idx === highlightedIndex ? "text-blue-800" : "text-gray-700"
                            }`}>{ingredient.batch || "-"}</td>
                            <td className={`py-3 px-4 transition-colors duration-200 ${
                              idx === highlightedIndex ? "text-blue-800" : "text-gray-700"
                            }`}>{ingredient.version || 1}</td>
                            <td className="py-3 px-4">
                              {/* Status keeps its own green/grey colours on the highlighted row too —
                                  the highlight used to repaint this pill, which erased the
                                  active/inactive signal on exactly the row being looked at. */}
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full transition-colors duration-200 ${
                                (Array.isArray(ingredient.status) ? ingredient.status.includes('active') : (ingredient.status as any) === true || (ingredient.status as any) === 'active')
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}>
                                {(Array.isArray(ingredient.status) ? ingredient.status.includes('active') : (ingredient.status as any) === true || (ingredient.status as any) === 'active') ? t("status.active") || "Active" : t("status.inactive") || "Inactive"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      {ingredients.length === 0 && !loading && (
                        <tr>
                          <td colSpan={5} className="text-center text-gray-500 py-12">
                            <div className="flex flex-col items-center gap-2">
                              <Search className="w-8 h-8 text-gray-300" />
                              <p>{t("search.noResults") || "No ingredients found"}</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}

                {/* Infinite scroll loading indicator */}
                {loading && page > 1 && (
                  <div className="flex items-center justify-center py-3 border-t border-gray-100">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-xs text-gray-600">Loading more...</span>
                  </div>
                )}

                {/* Invisible trigger for infinite scroll.
                    BUG 2 fix: only render once there is a non-empty list — otherwise
                    the sentinel sits in an empty scroll container, is immediately
                    intersecting, and appends a page onto an empty list. */}
                {ingredients.length > 0 && page < totalPages && (
                  <div
                    ref={loadingRef}
                    className="h-1 w-full"
                    style={{ opacity: 0 }}
                  />
                )}
              </div>

              <div className="flex items-center justify-center mt-4 pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-600">
                  {ingredients.length > 0 && (
                    <span>
                      {t("search.showingResults", { current: ingredients.length, total: absoluteTotal }) ||
                       `Showing ${ingredients.length} of ${absoluteTotal} results`}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default OSSearch;
